import { AnyAction } from '@reduxjs/toolkit'
import { get, keyBy, reject } from 'lodash'

import { FETCH_CONTEXT_USER_SUCCESS } from '@/components/App/actionTypes'
import {
  FETCH_TARGETING_RULE_SET_SUGGESTIONS_SUCCESS,
  RESOLVE_BULK_TARGETING_RULE_SETS_SUCCESS
} from '@/components/Publisher/actionTypes'
import { FETCH_ORGANIZATION_ADMINS_SUCCESS } from '@/pages/OrganizationSettingsPage/actionTypes'
import {
  ADD_SPACE_ADMIN_SUCCESS,
  FETCH_SPACE_MEMBERS_SUCCESS,
  REMOVE_SPACE_ADMIN_SUCCESS
} from '@/pages/OrganizationSpaceEditPage/actionTypes'
import { ADD_USER_SUCCESS, UPDATE_USER_SUCCESS } from '@/pages/OrganizationUserEditPage/actionTypes'
import { DELETE_USER_SUCCESS } from '@/pages/OrganizationUsersPage/actionTypes'
import { UPDATE_SPACE_ROLE_SUCCESS } from '@/pages/SpaceMemberEditPage/actionTypes'
import { REMOVE_MEMBER_FROM_SPACE_SUCCESS } from '@/pages/SpaceMembersPage/actionTypes'
import {
  ADD_SPACE_MEMBER_FROM_ROLE_SUCCESS,
  FETCH_SPACE_MEMBERS_BY_NOT_ROLE_SUCCESS,
  FETCH_SPACE_MEMBERS_BY_ROLE_SUCCESS
} from '@/pages/SpaceMembersRolesPage/actionTypes'
import { ADD_MEMBER_TO_SPACE_SUCCESS, FETCH_USER_ERROR, FETCH_USER_SUCCESS } from '@/store/actionTypes'

type UsersState = {
  byId: Record<string, any>
}

const initialState: UsersState = { byId: {} }

export default (state = initialState, action: AnyAction): UsersState => {
  // Adds users from the fetchUsersPaginated action
  const parsedAction = /^(organization\/users).*(_SUCCESS)$/.exec(action.type)

  if (parsedAction) {
    return { ...state, byId: { ...state.byId, ...keyBy(action.response.resources, 'id') } }
  }

  switch (action.type) {
    case FETCH_CONTEXT_USER_SUCCESS:
    case FETCH_USER_SUCCESS: {
      return { ...state, byId: { ...state.byId, [action.response.id]: action.response } }
    }
    case FETCH_USER_ERROR: {
      const userId = action.url.slice(Math.max(0, action.url.lastIndexOf('/') + 1))
      const UNKNOWN_MESSAGE_ID = 'unknown'
      const uknownUser = {
        email: UNKNOWN_MESSAGE_ID, //all users must have an email
        displayName: UNKNOWN_MESSAGE_ID
      }
      return { ...state, byId: { ...state.byId, [userId]: uknownUser } }
    }

    case FETCH_SPACE_MEMBERS_SUCCESS:
    case FETCH_ORGANIZATION_ADMINS_SUCCESS: {
      return { ...state, byId: { ...state.byId, ...keyBy(action.response, 'id') } }
    }

    case FETCH_TARGETING_RULE_SET_SUGGESTIONS_SUCCESS:
    case FETCH_SPACE_MEMBERS_BY_ROLE_SUCCESS:
    case FETCH_SPACE_MEMBERS_BY_NOT_ROLE_SUCCESS:
    case RESOLVE_BULK_TARGETING_RULE_SETS_SUCCESS: {
      return { ...state, byId: { ...state.byId, ...keyBy(action.response.resources, 'id') } }
    }

    case REMOVE_MEMBER_FROM_SPACE_SUCCESS: {
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.userId]: {
            ...state.byId[action.userId],
            spaces: reject(state.byId[action.userId].spaces, { spaceId: action.spaceId })
          }
        }
      }
    }

    case DELETE_USER_SUCCESS: {
      return { ...state, byId: reject(state.byId, { id: action.userId }) }
    }

    case REMOVE_SPACE_ADMIN_SUCCESS:
    case ADD_SPACE_ADMIN_SUCCESS:
    case ADD_MEMBER_TO_SPACE_SUCCESS:
    case UPDATE_SPACE_ROLE_SUCCESS:
    case ADD_SPACE_MEMBER_FROM_ROLE_SUCCESS:
    case ADD_USER_SUCCESS:
    case UPDATE_USER_SUCCESS: {
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.response.id]: { ...get(state.byId, action.response.id, {}), ...action.response }
        }
      }
    }

    default: {
      return state
    }
  }
}
